import {observer} from 'mobx-react-lite'
import React, {UIEventHandler, useEffect, useMemo} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Debouncer} from 'yougile-util'
import {ApiUi, UiSlot} from '@api/components'
import {FilePreview} from '@common/file-preview'
import {LinkButton} from '@components/common/base/buttons'
import {MessageForwardingPopup} from '@components/message-forwarding/message-forwarding-popup/message-forwarding-popup'
import {NoMessages} from '@components/right-panel/chat/chat-messages/no-messages'
import {TaskDescription} from '@components/right-panel/task-description'
import {useChatIdWithKind} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {ChatMessageUtils} from '@extensions/chat-message-utils'
import {useChatStore} from '../chat-store'
import {ChatTyping} from './chat-typing'
import {FirstUnread, MessageNew} from './message'

const TwChatItemMessages = tw.div<{$isExtensionComments: boolean}>`
  relative
  chat-item-messages
  ${p => (p.$isExtensionComments ? 'extension-comments' : '')}
  overflow-auto
  flex-1
  w-full
  order-2
  grow-1
  shrink-1
`

const TwShowMore = tw(LinkButton)`
  mt-12
  mb-8
  mx-auto
  !text-12
  !leading-4
  font-semibold
`

const TwMessagesWrap = tw.div<{$isNews: boolean}>`
  relative
  ${p =>
    cn(
      p.$isNews && cn('pt-16', 'shadow-border-t-1', 'shadow-grey-l60'),
      !p.$isNews && cn('min-h-full', 'pb-24')
    )}
`

const TwChatTyping = tw(ChatTyping)`
  absolute
  bottom-0
  left-0
`

const triggerDebouncer = new Debouncer(50)

const useScrollObserver = () => {
  const [state, store] = useChatStore()
  const container = store.messagesContainerElement

  const observer = useMemo(() => {
    if (!container) {
      return undefined
    }

    let lastSeen = Number.NEGATIVE_INFINITY
    return new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach(x => {
          if (x.isIntersecting) {
            const ts = x.target
              .closest('.message-item-new')
              .getAttribute('data-message-timestamp')
            ts && (lastSeen = Math.max(lastSeen, Number(ts)))
          }
        })
        store.setLastSeenScrolled(lastSeen)
      },
      {root: container}
    )
  }, [container, store, store.state.hub?.id])
  useEffect(() => () => observer?.disconnect(), [observer])
  useEffect(() => {
    if (container && observer) {
      container
        .querySelectorAll('.chat-message-bottom')
        .forEach(el => observer.observe(el))
    }
  }, [state.messagesList, container, observer])
}

export const ChatMessages = observer(() => {
  const [state, store] = useChatStore()
  const {kind} = useChatIdWithKind()

  useScrollObserver()

  const onScroll: UIEventHandler<HTMLDivElement> = (e): void => {
    const t = e.currentTarget

    triggerDebouncer.runNowButNotRightAfter(() => {
      // нужен setTimeout так как diff считается
      // до появления блока с сообщениями firstUnreadMessages
      setTimeout(() => {
        const scrollTop = t.scrollTop
        const height = t.scrollHeight
        const clientHeight = t.clientHeight

        const bottom = scrollTop + clientHeight

        const diff = height - bottom

        if (diff >= (clientHeight * 2) / 3) {
          store.setChatMessagesScroll('show-unseen-icon')
        } else if (diff <= 100) {
          store.setChatMessagesScroll('bottom')
        } else {
          store.setChatMessagesScroll(undefined)
        }
      }, 200)
    })
  }

  const idxLastImage = useMemo(() => {
    const msgs = state.messagesList
    let idxLastImage = msgs.length - 1
    for (; idxLastImage >= 0; idxLastImage--) {
      const msg = msgs[idxLastImage]
      if (FilePreview.rxImg.test(msg.text)) {
        break
      }
    }
    return idxLastImage
  }, [state.messagesList, state.messagesList.length])

  return (
    <TwChatItemMessages
      ref={store.setMessagesContainerElement}
      onScroll={onScroll}
      $isExtensionComments={store.isExtensionComments}
    >
      {store.isNews && <TaskDescription hub={state.hub} headerHeight={0} />}

      {store.showNoMessages && <NoMessages />}

      <TwMessagesWrap $isNews={store.isNews}>
        <div className="ui-cnt" />

        {store.isShowMoreVisible && (
          <TwShowMore
            color="primary"
            onClick={store.showMore}
            icon="IconMessages"
            noPaddings
          >
            {Lang.msg.showMore2}
          </TwShowMore>
        )}

        {state.messagesList.map((m, idx, ms) => (
          <React.Fragment key={m.timestamp}>
            {m.timestamp === state.firstUnread && (
              <FirstUnread timestamp={m.timestamp} />
            )}
            <MessageNew
              message={m}
              fullView={!ChatMessageUtils.isFromSameUser(m, ms[idx + 1])}
              hideAvatar={kind === 'personal'}
              animateShow={m.timestamp > state.newSince}
              lastImage={idxLastImage === idx}
            />
          </React.Fragment>
        ))}

        <UiSlot name="chat-messages-bottom" />
        <ApiUi category="chat-messages-bottom" id={state.hub?.id} />
        {!store.isNews && <TwChatTyping />}
      </TwMessagesWrap>

      {state.messageToForward && (
        <MessageForwardingPopup
          message={state.messageToForward}
          onSelect={store.setMessageToForward}
        />
      )}
    </TwChatItemMessages>
  )
})
